import { styled } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import * as Sentry from "@sentry/react";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import React from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { clarity } from "react-microsoft-clarity";
// import reportWebVitals from './reportWebVitals';
import { Provider as ReduxProvider } from "react-redux";
import { Router } from "react-router";

import { authService } from "@/common/services/auth";
import { tenantService } from "@/common/services/tenant";
import "@/common/ts/globals";

import App from "./App/App";
import { FeatureName } from "./common/constants/featureName";
import { BreadcrumbsContextProvider } from "./common/contexts/breadcrumbs";
import { OnboardingProvider } from "./common/contexts/onboarding";
import { AppHelper } from "./common/helpers/app";
import { EnvHelper } from "./common/helpers/env";
import { I18nHelper } from "./common/helpers/i18n";
import { TelemetryHelper } from "./common/helpers/telemetry";
import { chatHubService } from "./common/realtime/chatHubService";
import { dataUpdatesHubService } from "./common/realtime/dataUpdatesHubService";
import { notificationHubService } from "./common/realtime/notificationHubService";
import { enumService } from "./common/services/enum";
import tipsSchedulerService from "./common/services/tipsSchedulerService";
import { Stopwatch } from "./common/stopwatch";
import { NavDisplayProvider } from "./common/useNavDisplay";
import { appCommonConfig, clarityConfig, sentryConfig } from "./config/config";
import { featureManager } from "./config/features";
import { apiClient } from "./core/api/ApiClient";
// init Firebase early
import "./firebase";
import "./fonts.css";
import i18n from "./i18n";
import store, { history } from "./store";
import { prerenderConfigurationsStarted } from "./store/appCommon/slice";

// For debugging purposes, log when Vite fails to load dynamic imports.
// https://vite.dev/guide/build#load-error-handling
window.addEventListener("vite:preloadError", (e) => {
  console.log("index. vite:preloadError.", e);
});

AppHelper.defineCustomTypeExtensions();

Sentry.init({
  dsn: sentryConfig.sentryDsn,
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(EnvHelper.isProductionAny ? [Sentry.replayIntegration({})] : []),
    Sentry.browserApiErrorsIntegration(),
    Sentry.captureConsoleIntegration({
      levels: ["warn", "error"],
    }),
    Sentry.extraErrorDataIntegration(),
  ],
  // Tracing
  tracesSampleRate: sentryConfig.tracesSampleRate,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // It should not be allowed to propagate traces to all URLs such as OAuth provider because authentication may fail
  tracePropagationTargets: sentryConfig.tracePropagationTargets,

  // Session Replay
  // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
  // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
  // Set environment
  environment: appCommonConfig.customEnv,
  enabled: sentryConfig.isEnabled,
});
TelemetryHelper.setSentryTagsOnAppInit();

const MaterialDesignContentStyled = styled(MaterialDesignContent)(({ theme }) => ({
  "&.notistack-MuiContent-success": {
    backgroundColor: theme.palette.success.main,
  },
  "&.notistack-MuiContent-info": {
    backgroundColor: theme.palette.info.main,
  },
  "&.notistack-MuiContent-warning": {
    backgroundColor: theme.palette.warning.main,
  },
  "&.notistack-MuiContent-error": {
    backgroundColor: theme.palette.error.main,
  },
}));

// console.log("index", moment(performance.now()).format("HH:mm:ss.SSS"));

(async () => {
  const stopwatch = Stopwatch.startNew();

  AppHelper.logAppInfoAtStartup();
  tenantService.initRedirectToTenantUrl();

  if (clarityConfig.enabled) {
    clarity.init(clarityConfig.trackingCode);
  }

  await store.dispatch(prerenderConfigurationsStarted());
  enumService.handleEnumCatalogOnAppInit();

  // configure API client with local user i18n settings
  const i18nSettings = I18nHelper.getUserI18nSettingsLocal();
  if (i18nSettings) {
    apiClient.updateUserI18nSettings(i18nSettings);
  }

  authService.on("authenticationStateChange", async ({ isAuthenticated }) => {
    console.log("on authenticationStateChange.", { isAuthenticated });

    if (isAuthenticated) {
      chatHubService.connect();
      notificationHubService.connect();
      dataUpdatesHubService.connect();
    } else {
      chatHubService.disconnect();
      notificationHubService.disconnect();
      dataUpdatesHubService.disconnect();
    }
  });

  if (featureManager.isEnabled(FeatureName.AiAssistant)) {
    const tipsIdle = 8 * 60 * 60 * 1000; // 8 hours
    const tipsInterval = 60 * 60 * 1000; // check every hour
    tipsSchedulerService.start(tipsIdle, tipsInterval);
  }

  const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

  root.render(
    // https://react.dev/reference/react/StrictMode
    <React.StrictMode>
      <ReduxProvider store={store}>
        <Router history={history}>
          <I18nextProvider i18n={i18n}>
            {/* https://mui.com/x/react-date-pickers/getting-started/#setup-your-date-library-adapter  */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <SnackbarProvider
                Components={{
                  success: MaterialDesignContentStyled,
                  info: MaterialDesignContentStyled,
                  warning: MaterialDesignContentStyled,
                  error: MaterialDesignContentStyled,
                }}
                maxSnack={4}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <BreadcrumbsContextProvider>
                  <NavDisplayProvider>
                    <OnboardingProvider>
                      <App />
                    </OnboardingProvider>
                  </NavDisplayProvider>
                </BreadcrumbsContextProvider>
              </SnackbarProvider>
            </LocalizationProvider>
          </I18nextProvider>
        </Router>
      </ReduxProvider>
    </React.StrictMode>,
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  // reportWebVitals();

  stopwatch.stop();
  console.info(`Startup. App started.`, {
    elapsedMilliseconds: stopwatch.elapsedMilliseconds,
    elapsedSeconds: stopwatch.elapsedSeconds,
  });
})();
